import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../../../components/ui/dialog";
import { toast } from "sonner";
import {
  Inbox, Plus, Pencil, Trash2, Filter, Zap, Pin, PinOff, Play,
  DollarSign, Truck, Package, Clock, Tag, ShieldCheck,
} from "lucide-react";

/**
 * OrderQueuesPanel — smart + manual order buckets for the Fulfillment tab.
 *
 * Sellers create named queues combining smart filters (statuses, carriers,
 * value, age, tags, buyers) with manual pin/exclude overrides. Each queue
 * can optionally auto-apply a Playbook, route to a warehouse, and be
 * assigned to a team member.
 *
 * Backend: /api/dropship/order-queues (see routes/dropship/order_queues.py)
 */

const COLOR_MAP = {
  indigo:  { chip: "bg-indigo-100 text-indigo-800 border-indigo-200",  dot: "bg-indigo-500" },
  emerald: { chip: "bg-emerald-100 text-emerald-800 border-emerald-200", dot: "bg-emerald-500" },
  amber:   { chip: "bg-amber-100 text-amber-800 border-amber-200",       dot: "bg-amber-500" },
  rose:    { chip: "bg-rose-100 text-rose-800 border-rose-200",          dot: "bg-rose-500" },
  sky:     { chip: "bg-sky-100 text-sky-800 border-sky-200",             dot: "bg-sky-500" },
  slate:   { chip: "bg-slate-100 text-slate-700 border-slate-200",       dot: "bg-slate-500" },
};

const STATUS_CHOICES = ["new", "forwarded", "packing", "shipped", "delivered"];

const emptyDraft = () => ({
  id: null,
  name: "",
  description: "",
  color: "indigo",
  filters: {
    statuses: [],
    carriers: [],
    buyer_ids: [],
    store_ids: [],
    tags_any: [],
    min_retail: "",
    max_retail: "",
    created_within_hours: "",
    signature_required: null,
    destination_states: [],
  },
  playbook_id: "",
  warehouse_id: "",
  assignee_id: "",
});

function filterSummary(f) {
  const bits = [];
  if (f?.statuses?.length) bits.push(`status: ${f.statuses.join(", ")}`);
  if (f?.carriers?.length) bits.push(`carrier: ${f.carriers.join(", ")}`);
  if (f?.min_retail != null && f.min_retail !== "") bits.push(`≥ $${f.min_retail}`);
  if (f?.max_retail != null && f.max_retail !== "") bits.push(`≤ $${f.max_retail}`);
  if (f?.created_within_hours) bits.push(`< ${f.created_within_hours}h old`);
  if (f?.tags_any?.length) bits.push(`tag: ${f.tags_any.join(", ")}`);
  if (f?.signature_required === true) bits.push("signature");
  if (f?.destination_states?.length) bits.push(`→ ${f.destination_states.join(", ")}`);
  return bits.length ? bits.join(" · ") : "No smart filters — manual only";
}

export default function OrderQueuesPanel() {
  const [queues, setQueues] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [playbooks, setPlaybooks] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);

  const selected = useMemo(
    () => queues.find(q => q.id === selectedId) || null,
    [queues, selectedId],
  );

  const loadQueues = useCallback(async () => {
    try {
      const { data } = await api.get("/dropship/order-queues");
      setQueues(data?.queues || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load queues");
    }
  }, []);

  const loadOrders = useCallback(async (qid) => {
    if (!qid) { setOrders([]); return; }
    setOrdersLoading(true);
    try {
      const { data } = await api.get(`/dropship/order-queues/${qid}/orders`);
      setOrders(data?.orders || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load orders");
    } finally { setOrdersLoading(false); }
  }, []);

  useEffect(() => { loadQueues(); }, [loadQueues]);

  useEffect(() => { loadOrders(selectedId); }, [selectedId, loadOrders]);

  useEffect(() => {
    if (!editorOpen) return;
    (async () => {
      try {
        const [pbRes, stRes] = await Promise.all([
          api.get("/dropship/playbooks").catch(() => ({ data: { playbooks: [] } })),
          api.get("/dropship/fulfillment-settings").catch(() => ({ data: {} })),
        ]);
        setPlaybooks(pbRes.data?.playbooks || []);
        setWarehouses(stRes.data?.ship_from_addresses || []);
      } catch { /* silent */ }
    })();
  }, [editorOpen]);

  const openCreate = () => { setDraft(emptyDraft()); setEditorOpen(true); };

  const openEdit = (q) => {
    setDraft({
      id: q.id,
      name: q.name || "",
      description: q.description || "",
      color: q.color || "indigo",
      filters: {
        statuses: q.filters?.statuses || [],
        carriers: q.filters?.carriers || [],
        buyer_ids: q.filters?.buyer_ids || [],
        store_ids: q.filters?.store_ids || [],
        tags_any: q.filters?.tags_any || [],
        min_retail: q.filters?.min_retail ?? "",
        max_retail: q.filters?.max_retail ?? "",
        created_within_hours: q.filters?.created_within_hours ?? "",
        signature_required: q.filters?.signature_required ?? null,
        destination_states: q.filters?.destination_states || [],
      },
      playbook_id: q.playbook_id || "",
      warehouse_id: q.warehouse_id || "",
      assignee_id: q.assignee_id || "",
    });
    setEditorOpen(true);
  };

  const saveDraft = async () => {
    if (!draft.name?.trim()) return toast.error("Name required");
    setSaving(true);
    const body = {
      name: draft.name.trim(),
      description: draft.description || "",
      color: draft.color || "indigo",
      filters: {
        statuses: draft.filters.statuses || [],
        carriers: draft.filters.carriers || [],
        buyer_ids: draft.filters.buyer_ids || [],
        store_ids: draft.filters.store_ids || [],
        tags_any: draft.filters.tags_any || [],
        min_retail: draft.filters.min_retail === "" ? null : Number(draft.filters.min_retail),
        max_retail: draft.filters.max_retail === "" ? null : Number(draft.filters.max_retail),
        created_within_hours: draft.filters.created_within_hours === "" ? null : Number(draft.filters.created_within_hours),
        signature_required: draft.filters.signature_required,
        destination_states: draft.filters.destination_states || [],
      },
      playbook_id: draft.playbook_id || null,
      warehouse_id: draft.warehouse_id || null,
      assignee_id: draft.assignee_id || null,
    };
    try {
      if (draft.id) {
        await api.put(`/dropship/order-queues/${draft.id}`, body);
        toast.success("Queue updated");
      } else {
        const { data } = await api.post("/dropship/order-queues", body);
        toast.success("Queue created");
        setSelectedId(data.id);
      }
      setEditorOpen(false);
      await loadQueues();
      if (draft.id) await loadOrders(draft.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const deleteQueue = async (q) => {
    if (!window.confirm(`Delete queue "${q.name}"?`)) return;
    try {
      await api.delete(`/dropship/order-queues/${q.id}`);
      toast.success("Queue deleted");
      if (selectedId === q.id) setSelectedId(null);
      await loadQueues();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const togglePin = async (order) => {
    if (!selected) return;
    const url = order._pinned
      ? `/dropship/order-queues/${selected.id}/unpin/${order.id}`
      : `/dropship/order-queues/${selected.id}/pin/${order.id}`;
    try {
      await api.post(url);
      toast.success(order._pinned ? "Unpinned" : "Pinned");
      await Promise.all([loadOrders(selected.id), loadQueues()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pin failed");
    }
  };

  const excludeOrder = async (order) => {
    if (!selected) return;
    try {
      await api.post(`/dropship/order-queues/${selected.id}/exclude/${order.id}`);
      toast.success("Excluded from queue");
      await Promise.all([loadOrders(selected.id), loadQueues()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Exclude failed");
    }
  };

  const runActions = async () => {
    if (!selected) return;
    const hasAny = selected.playbook_id || selected.warehouse_id || selected.assignee_id;
    if (!hasAny) return toast.error("Attach a playbook, warehouse, or assignee first");
    setApplying(true);
    try {
      const { data } = await api.post(`/dropship/order-queues/${selected.id}/apply-actions`);
      const parts = [];
      if (data.playbook_applied) parts.push(`playbook → ${data.playbook_modified || 0}`);
      if (data.warehouse_set) parts.push("warehouse set");
      if (data.assignee_set) parts.push("assignee set");
      toast.success(`Applied to ${data.order_count || 0} order${data.order_count === 1 ? "" : "s"}${parts.length ? ` (${parts.join(", ")})` : ""}`);
      await loadOrders(selected.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally { setApplying(false); }
  };

  return (
    <div className="space-y-5" data-testid="order-queues-panel">
      {/* Header + chip strip */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[15px] font-semibold text-foreground">Order queues</div>
          <div className="text-xs text-foreground/60 mt-0.5">
            Smart + manual buckets for organizing today&apos;s shipments. Attach a playbook or warehouse to auto-route.
          </div>
        </div>
        <Button size="sm" onClick={openCreate} data-testid="new-queue-btn">
          <Plus className="w-3.5 h-3.5 mr-1.5"/>New queue
        </Button>
      </div>

      {/* Chip strip of queues */}
      <div className="flex flex-wrap items-center gap-2">
        {queues.map(q => {
          const c = COLOR_MAP[q.color] || COLOR_MAP.indigo;
          const active = selectedId === q.id;
          return (
            <button
              key={q.id}
              onClick={() => setSelectedId(active ? null : q.id)}
              data-testid={`queue-chip-${q.id}`}
              className={`inline-flex items-center gap-2 px-3 h-8 rounded-full border text-[12px] font-medium transition-all ${
                active
                  ? "border-foreground text-foreground bg-foreground/5"
                  : `${c.chip} hover:brightness-95`
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${c.dot}`}/>
              {q.name}
              <span className="tabular-nums text-[11px] opacity-70">{q.count ?? 0}</span>
            </button>
          );
        })}
        {queues.length === 0 && (
          <div className="text-xs text-foreground/50">No queues yet — create one to organize your fulfillment work.</div>
        )}
      </div>

      {/* Selected queue detail */}
      {selected && (
        <div className="rounded-xl border border-border bg-card p-4 space-y-4" data-testid="queue-detail">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${(COLOR_MAP[selected.color] || COLOR_MAP.indigo).dot}`}/>
                <div className="text-[14px] font-semibold text-foreground">{selected.name}</div>
                <span className="mono text-[10px] uppercase tracking-[0.14em] text-foreground/40">{selected.count} orders</span>
              </div>
              {selected.description && (
                <div className="text-xs text-foreground/60 mt-1">{selected.description}</div>
              )}
              <div className="text-[11px] text-foreground/50 mt-1.5 flex items-center gap-1.5">
                <Filter className="w-3 h-3"/> {filterSummary(selected.filters)}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="outline" onClick={() => openEdit(selected)} data-testid="edit-queue-btn">
                <Pencil className="w-3.5 h-3.5"/>
              </Button>
              <Button size="sm" variant="outline" onClick={() => deleteQueue(selected)} data-testid="delete-queue-btn">
                <Trash2 className="w-3.5 h-3.5 text-rose-600"/>
              </Button>
              <Button size="sm" onClick={runActions} disabled={applying} data-testid="apply-actions-btn">
                <Play className="w-3.5 h-3.5 mr-1.5"/>{applying ? "Applying…" : "Apply actions"}
              </Button>
            </div>
          </div>

          {/* Attached actions badges */}
          <div className="flex flex-wrap items-center gap-2">
            {selected.playbook_id && (
              <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-md bg-indigo-50 text-indigo-800 text-[10.5px] font-medium border border-indigo-200">
                <Zap className="w-3 h-3"/>
                Playbook: {playbooks.find(p => p.id === selected.playbook_id)?.name || selected.playbook_id.slice(0, 8)}
              </span>
            )}
            {selected.warehouse_id && (
              <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-md bg-emerald-50 text-emerald-800 text-[10.5px] font-medium border border-emerald-200">
                <Truck className="w-3 h-3"/>
                Warehouse: {warehouses.find(w => w.id === selected.warehouse_id)?.name || selected.warehouse_id.slice(0, 8)}
              </span>
            )}
            {selected.assignee_id && (
              <span className="inline-flex items-center gap-1.5 h-6 px-2 rounded-md bg-sky-50 text-sky-800 text-[10.5px] font-medium border border-sky-200">
                <Inbox className="w-3 h-3"/>
                Assignee: {selected.assignee_id}
              </span>
            )}
            {!selected.playbook_id && !selected.warehouse_id && !selected.assignee_id && (
              <span className="text-[11px] text-foreground/40">No actions attached — edit queue to add.</span>
            )}
          </div>

          {/* Orders list */}
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="grid grid-cols-[24px_1fr_100px_90px_100px_130px] gap-2 h-9 px-3 items-center bg-muted/30 text-[10.5px] uppercase tracking-[0.14em] text-foreground/50 font-medium">
              <div/>
              <div>Order</div>
              <div>Status</div>
              <div className="text-right">Value</div>
              <div>Buyer</div>
              <div className="text-right">Actions</div>
            </div>
            {ordersLoading ? (
              <div className="p-6 text-center text-xs text-foreground/50">Loading orders…</div>
            ) : orders.length === 0 ? (
              <div className="p-8 text-center text-xs text-foreground/50">
                No orders match this queue&apos;s filters yet.
              </div>
            ) : (
              orders.map(o => (
                <div key={o.id} className="grid grid-cols-[24px_1fr_100px_90px_100px_130px] gap-2 h-11 px-3 items-center border-t border-border text-[12.5px] hover:bg-muted/20">
                  <div>
                    {o._pinned && <Pin className="w-3.5 h-3.5 text-amber-600" title="Manually pinned"/>}
                  </div>
                  <div className="min-w-0 truncate font-medium text-foreground">{o.code}</div>
                  <div className="text-xs text-foreground/70 capitalize">{o.status}</div>
                  <div className="text-right tabular-nums text-foreground/80">${Number(o.retail_price || 0).toFixed(2)}</div>
                  <div className="text-xs text-foreground/70 truncate">{o.buyer_name || "—"}</div>
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => togglePin(o)}
                      className="h-6 px-1.5 rounded text-[10.5px] font-medium text-foreground/70 hover:bg-muted/60 inline-flex items-center gap-1"
                      title={o._pinned ? "Remove pin" : "Pin to queue"}
                      data-testid={`toggle-pin-${o.id}`}
                    >
                      {o._pinned ? <PinOff className="w-3 h-3"/> : <Pin className="w-3 h-3"/>}
                      {o._pinned ? "Unpin" : "Pin"}
                    </button>
                    {!o._pinned && (
                      <button
                        onClick={() => excludeOrder(o)}
                        className="h-6 px-1.5 rounded text-[10.5px] font-medium text-rose-600/80 hover:bg-rose-50"
                        title="Exclude from this queue"
                        data-testid={`exclude-${o.id}`}
                      >
                        Exclude
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Editor dialog */}
      <QueueEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        draft={draft}
        setDraft={setDraft}
        playbooks={playbooks}
        warehouses={warehouses}
        saving={saving}
        onSave={saveDraft}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Editor dialog
// ─────────────────────────────────────────────────────────────────────
function QueueEditorDialog({
  open, onOpenChange, draft, setDraft, playbooks, warehouses, saving, onSave,
}) {
  const setFilter = (k, v) => setDraft(d => ({ ...d, filters: { ...d.filters, [k]: v } }));

  const toggleStatus = (s) => {
    setFilter("statuses",
      draft.filters.statuses.includes(s)
        ? draft.filters.statuses.filter(x => x !== s)
        : [...draft.filters.statuses, s]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="queue-editor-dialog">
        <DialogHeader>
          <DialogTitle>{draft.id ? "Edit queue" : "New queue"}</DialogTitle>
          <DialogDescription>
            Combine smart filters with manual pins. Attach a Playbook or warehouse to auto-apply when you press &quot;Apply actions&quot;.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Basics */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Name</Label>
              <Input
                value={draft.name}
                onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                placeholder="e.g. Priority > $500"
                data-testid="queue-name-input"
              />
            </div>
            <div>
              <Label className="text-xs">Color</Label>
              <div className="flex items-center gap-1.5 mt-1.5">
                {Object.keys(COLOR_MAP).map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setDraft(d => ({ ...d, color: c }))}
                    className={`w-6 h-6 rounded-full ${COLOR_MAP[c].dot} ${draft.color === c ? "ring-2 ring-offset-2 ring-foreground" : ""}`}
                    aria-label={c}
                  />
                ))}
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Input
              value={draft.description}
              onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
              placeholder="What does this queue represent?"
            />
          </div>

          {/* Smart filters */}
          <div className="rounded-lg border border-border p-3 space-y-3">
            <div className="text-[11px] uppercase tracking-[0.14em] text-foreground/50 font-medium flex items-center gap-1.5">
              <Filter className="w-3 h-3"/> Smart filters
            </div>

            <div>
              <Label className="text-xs flex items-center gap-1"><Package className="w-3 h-3"/>Statuses</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {STATUS_CHOICES.map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleStatus(s)}
                    className={`h-7 px-2.5 rounded-full text-[11px] font-medium border capitalize ${
                      draft.filters.statuses.includes(s)
                        ? "bg-foreground text-background border-foreground"
                        : "bg-background text-foreground/70 border-border hover:border-foreground/40"
                    }`}
                  >{s}</button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs flex items-center gap-1"><DollarSign className="w-3 h-3"/>Min value</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={draft.filters.min_retail}
                  onChange={e => setFilter("min_retail", e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1"><DollarSign className="w-3 h-3"/>Max value</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={draft.filters.max_retail}
                  onChange={e => setFilter("max_retail", e.target.value)}
                  placeholder="No limit"
                />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1"><Clock className="w-3 h-3"/>Age &lt; (hours)</Label>
                <Input
                  type="number" min="1" step="1"
                  value={draft.filters.created_within_hours}
                  onChange={e => setFilter("created_within_hours", e.target.value)}
                  placeholder="e.g. 24"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs flex items-center gap-1"><Truck className="w-3 h-3"/>Carriers (comma-sep)</Label>
                <Input
                  value={(draft.filters.carriers || []).join(", ")}
                  onChange={e => setFilter("carriers", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                  placeholder="ups, fedex, usps"
                />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1"><Tag className="w-3 h-3"/>Tags (any of)</Label>
                <Input
                  value={(draft.filters.tags_any || []).join(", ")}
                  onChange={e => setFilter("tags_any", e.target.value.split(",").map(s => s.trim().toLowerCase()).filter(Boolean))}
                  placeholder="urgent, b2b, gift"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Destination states (2-letter, comma-sep)</Label>
                <Input
                  value={(draft.filters.destination_states || []).join(", ")}
                  onChange={e => setFilter("destination_states", e.target.value.split(",").map(s => s.trim().toUpperCase()).filter(Boolean))}
                  placeholder="CA, OR, WA"
                />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1"><ShieldCheck className="w-3 h-3"/>Signature required</Label>
                <select
                  value={draft.filters.signature_required === null ? "" : String(draft.filters.signature_required)}
                  onChange={e => setFilter("signature_required", e.target.value === "" ? null : e.target.value === "true")}
                  className="w-full h-9 rounded-md border border-input bg-background text-sm px-2"
                >
                  <option value="">Any</option>
                  <option value="true">Only signature-required</option>
                  <option value="false">Only non-signature</option>
                </select>
              </div>
            </div>
          </div>

          {/* Actions attached to the queue */}
          <div className="rounded-lg border border-border p-3 space-y-3">
            <div className="text-[11px] uppercase tracking-[0.14em] text-foreground/50 font-medium flex items-center gap-1.5">
              <Zap className="w-3 h-3"/> Attached actions (optional)
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Auto-apply Playbook</Label>
                <select
                  value={draft.playbook_id}
                  onChange={e => setDraft(d => ({ ...d, playbook_id: e.target.value }))}
                  className="w-full h-9 rounded-md border border-input bg-background text-sm px-2"
                  data-testid="queue-playbook-select"
                >
                  <option value="">— None —</option>
                  {playbooks.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Route to warehouse</Label>
                <select
                  value={draft.warehouse_id}
                  onChange={e => setDraft(d => ({ ...d, warehouse_id: e.target.value }))}
                  className="w-full h-9 rounded-md border border-input bg-background text-sm px-2"
                  data-testid="queue-warehouse-select"
                >
                  <option value="">— None —</option>
                  {warehouses.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label className="text-xs">Assign to team member (email or user id)</Label>
              <Input
                value={draft.assignee_id}
                onChange={e => setDraft(d => ({ ...d, assignee_id: e.target.value }))}
                placeholder="picker@yourwarehouse.com"
                data-testid="queue-assignee-input"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} data-testid="save-queue-btn">
            {saving ? "Saving…" : (draft.id ? "Save changes" : "Create queue")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
